import React from 'react';
import PropTypes from 'prop-types';
import { FaFileExcel, FaFilePdf, FaHighlighter, FaExchangeAlt } from 'react-icons/fa';

// Toolbar above the PIF detail tabs: pick a baseline revision/CO, toggle in-view
// highlighting, and export the changes (Excel / PDF).
const PIFCompareBar = ({ targets = [], against, onAgainstChange, highlight, onToggleHighlight, onExport, diff, exporting, error }) => {
  const summary = diff?.summary;

  return (
    <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-x-4 tw-gap-y-2 tw-px-4 tw-py-2.5 tw-bg-slate-50 tw-border-b tw-border-slate-200">
      {/* ── Baseline picker ── */}
      <div className="tw-flex tw-items-center tw-gap-2">
        <span className="tw-inline-flex tw-items-center tw-justify-center tw-w-6 tw-h-6 tw-rounded-md tw-bg-primary-100 tw-text-primary-600 tw-flex-shrink-0">
          <FaExchangeAlt size={11} />
        </span>
        <span className="tw-text-xs tw-font-semibold tw-text-slate-500 tw-uppercase tw-tracking-wide">Compare with</span>
        <select
          value={against || ''}
          onChange={(e) => onAgainstChange(e.target.value || null)}
          className="tw-h-8 tw-px-2.5 tw-text-sm tw-font-medium tw-text-slate-700 tw-bg-white tw-border tw-border-slate-300 tw-rounded tw-cursor-pointer hover:tw-border-primary-400 focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-primary-400 focus:tw-border-transparent"
        >
          <option value="">Previous (auto)</option>
          {targets.map((t) => (
            <option key={t.adaptation_id} value={t.adaptation_id}>
              {t.label}{t.pif_number ? ` · ${t.pif_number}` : ''}
            </option>
          ))}
        </select>
      </div>

      {/* ── Summary ── */}
      {diff && (
        <div className="tw-flex tw-items-center tw-gap-1.5">
          <span className="tw-text-xs tw-text-slate-400 tw-font-medium">{diff.base?.label} → {diff.compare?.label}</span>
          {summary?.total > 0 ? (
            <>
              <span className="tw-inline-flex tw-items-center tw-px-2 tw-py-0.5 tw-rounded-xl tw-bg-white tw-border tw-border-slate-200 tw-text-xs tw-font-semibold tw-text-slate-600">
                {summary.total} {summary.total === 1 ? 'change' : 'changes'}
              </span>
              {summary.added > 0 && (
                <span className="tw-inline-flex tw-items-center tw-px-1.5 tw-py-0.5 tw-rounded-xl tw-bg-emerald-50 tw-border tw-border-emerald-200 tw-text-xs tw-font-semibold tw-text-emerald-700">+{summary.added}</span>
              )}
              {summary.removed > 0 && (
                <span className="tw-inline-flex tw-items-center tw-px-1.5 tw-py-0.5 tw-rounded-xl tw-bg-rose-50 tw-border tw-border-rose-200 tw-text-xs tw-font-semibold tw-text-rose-700">−{summary.removed}</span>
              )}
              {summary.changed > 0 && (
                <span className="tw-inline-flex tw-items-center tw-px-1.5 tw-py-0.5 tw-rounded-xl tw-bg-amber-50 tw-border tw-border-amber-200 tw-text-xs tw-font-semibold tw-text-amber-700">~{summary.changed}</span>
              )}
            </>
          ) : (
            <span className="tw-inline-flex tw-items-center tw-px-2 tw-py-0.5 tw-rounded-xl tw-bg-white tw-border tw-border-slate-200 tw-text-xs tw-font-medium tw-text-slate-400">No changes</span>
          )}
        </div>
      )}
      {error && <span className="tw-text-xs tw-text-rose-500">{error}</span>}

      {/* ── Actions (hidden when there are no changes) ── */}
      {(!summary || summary.total > 0) && (
      <div className="tw-ml-auto tw-flex tw-items-center tw-gap-2">
        <button
          type="button"
          onClick={onToggleHighlight}
          title={highlight ? 'Hide highlighting' : 'Highlight changed fields in view'}
          className={`tw-inline-flex tw-items-center tw-gap-1.5 tw-h-8 tw-px-3 tw-rounded tw-text-sm tw-font-medium tw-border tw-cursor-pointer tw-transition-colors ${
            highlight
              ? 'tw-bg-amber-400 tw-border-amber-400 tw-text-white hover:tw-bg-amber-500'
              : 'tw-bg-white tw-border-slate-300 tw-text-slate-600 hover:tw-border-amber-300 hover:tw-text-amber-600'
          }`}
        >
          <FaHighlighter size={12} /> Show Highlights
        </button>

        <div className="tw-h-5 tw-w-px tw-bg-slate-200" />

        <button
          type="button" disabled={exporting || !diff} onClick={() => onExport('xlsx')} title="Export changes to Excel"
          className="tw-inline-flex tw-items-center tw-gap-1.5 tw-h-8 tw-px-3 tw-rounded tw-text-sm tw-font-medium tw-border tw-border-emerald-200 tw-bg-white tw-text-emerald-700 hover:tw-bg-emerald-50 tw-cursor-pointer disabled:tw-opacity-40"
        >
          <FaFileExcel size={13} /> Excel
        </button>
        <button
          type="button" disabled={exporting || !diff} onClick={() => onExport('pdf')} title="Export changes to PDF"
          className="tw-inline-flex tw-items-center tw-gap-1.5 tw-h-8 tw-px-3 tw-rounded tw-text-sm tw-font-medium tw-border tw-border-rose-200 tw-bg-white tw-text-rose-700 hover:tw-bg-rose-50 tw-cursor-pointer disabled:tw-opacity-40"
        >
          <FaFilePdf size={13} /> PDF
        </button>
      </div>
      )}
    </div>
  );
};

PIFCompareBar.propTypes = {
  targets: PropTypes.array,
  against: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onAgainstChange: PropTypes.func.isRequired,
  highlight: PropTypes.bool,
  onToggleHighlight: PropTypes.func.isRequired,
  onExport: PropTypes.func.isRequired,
  diff: PropTypes.object,
  exporting: PropTypes.bool,
  error: PropTypes.string,
};

export default PIFCompareBar;
